import { useSyncExternalStore } from 'react';

export type Theme = 'light' | 'dark';

/** Explicit choice from the top-bar toggle; while absent the app follows the OS setting. */
const STORAGE_KEY = 'hearth.theme';
const DARK_QUERY = '(prefers-color-scheme: dark)';
/** Top bar colour (--color-surface) per theme: the phone's status bar takes it when the app runs from the home screen. */
const BAR_COLOR: Record<Theme, string> = { light: '#ffffff', dark: '#131823' };

let current: Theme = 'light';
const listeners = new Set<() => void>();

function savedTheme(): Theme | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value === 'light' || value === 'dark' ? value : null;
  } catch {
    return null; // Storage disabled (privacy mode) — the OS preference still applies.
  }
}

function systemTheme(): Theme {
  return window.matchMedia?.(DARK_QUERY).matches ? 'dark' : 'light';
}

function applyTheme(theme: Theme): void {
  current = theme;
  const root = document.documentElement;
  // Switch in a single frame: otherwise everything with `transition-colors` fades while the rest of the page jumps.
  const freeze = document.createElement('style');
  freeze.textContent = '*,*::before,*::after{transition:none!important}';
  document.head.append(freeze);
  root.classList.toggle('dark', theme === 'dark');
  root.style.colorScheme = theme;
  document.querySelector('meta[name="color-scheme"]')?.setAttribute('content', theme);
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', BAR_COLOR[theme]);
  void getComputedStyle(root).color; // Flush styles while transitions are off.
  freeze.remove();
  for (const listener of listeners) listener();
}

/**
 * Applies the saved (else OS) theme to <html>. main.tsx calls this before the first render because the CSP forbids the
 * usual inline <head> script. Without a saved choice the theme follows OS changes live; choices made in another tab sync.
 */
export function initTheme(): void {
  applyTheme(savedTheme() ?? systemTheme());
  window.matchMedia?.(DARK_QUERY).addEventListener('change', () => {
    if (!savedTheme()) applyTheme(systemTheme());
  });
  window.addEventListener('storage', (event) => {
    if (event.key === STORAGE_KEY) applyTheme(savedTheme() ?? systemTheme());
  });
}

/** Applies and remembers an explicit choice. */
export function setTheme(theme: Theme): void {
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // Not persisted, but still applied to this page.
  }
  applyTheme(theme);
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useTheme(): Theme {
  return useSyncExternalStore(subscribe, () => current);
}
